import { useEffect, useState } from 'react'
import { AlertTriangle, Clock } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

type TrialState =
  | { kind: 'hidden' }
  | { kind: 'none' }
  | { kind: 'days_left'; days: number }

/**
 * Banner de trial no topo do layout autenticado.
 * Estados: >7 dias neutro · ≤7 âmbar · ≤0 vermelho ("Trial encerrado").
 * Sem subscription trialing → sem banner (orgs manuais antigas / ativas).
 */
export function TrialBanner() {
  const { currentOrganization } = useAuth()
  const [state, setState] = useState<TrialState>({ kind: 'hidden' })

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!currentOrganization) {
        setState({ kind: 'hidden' })
        return
      }
      const { data } = await supabase
        .from('subscriptions')
        .select('status, trial_end')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'trialing')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (cancelled) return

      if (!data?.trial_end) {
        setState({ kind: 'none' })
        return
      }

      const msLeft = new Date(data.trial_end).getTime() - Date.now()
      const days = Math.ceil(msLeft / 86_400_000)
      setState({ kind: 'days_left', days })
    }

    load()
    return () => {
      cancelled = true
    }
  }, [currentOrganization])

  if (state.kind !== 'days_left') return null

  if (state.days <= 0) {
    return (
      <div className="w-full bg-red-600 text-white px-4 py-2.5 text-sm flex items-center justify-center gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>
          <strong>Trial encerrado.</strong> Fale com a gente para ativar seu plano e
          continuar acompanhando seus pacientes.
        </span>
      </div>
    )
  }

  if (state.days <= 7) {
    return (
      <div className="w-full bg-amber-400 text-amber-950 px-4 py-2.5 text-sm flex items-center justify-center gap-2">
        <Clock className="w-4 h-4 shrink-0" />
        <span>
          Faltam <strong>{state.days} {state.days === 1 ? 'dia' : 'dias'}</strong> do seu
          trial. Para não perder o acompanhamento, ative seu plano.
        </span>
      </div>
    )
  }

  return (
    <div className="w-full bg-teal-50 text-teal-900 px-4 py-2.5 text-sm flex items-center justify-center gap-2 border-b border-teal-100">
      <Clock className="w-4 h-4 shrink-0" />
      <span>
        Você está no trial de <strong>{state.days} dias</strong>. Aproveite todos os
        recursos: cadastre pacientes e convide sua equipe.
      </span>
    </div>
  )
}
